import '@/components/styles/_common.scss';

import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { AppProps } from 'next/app';
import getConfig from 'next/config';
import Head from 'next/head';
import Router from 'next/router';

import { TPagePropsForJwtAuthentication } from '@/common/next/authenticate';
import { CONFLICT_ERROR, INTERNAL_SERVER_ERROR, NOT_FOUND_ERROR } from '@/constants/commons/statusCode';
import { useConciergeList } from '@/hooks/commons/common/useConciergePulldown';
import { useLoginUserName } from '@/hooks/useLoginUserName';
import { usePageChanged } from '@/hooks/usePageChanged';
import { TLwtApiResponse, TResponseBodyOnError } from '@/infrastructures/api/LWTApi';
import { TMeta } from '@/types/responses/meta';
import { LogUtil } from '@/util/logUtil';

const { publicRuntimeConfig } = getConfig();

Sentry.init({
  dsn: publicRuntimeConfig.NEXT_PUBLIC_SENTRY_DSN,
  environment: publicRuntimeConfig.ENV,
  integrations: [new Integrations.BrowserTracing()],
  tracesSampleRate: 1.0,
});

const App = ({ Component, pageProps }: AppProps) => {
  usePageChanged();
  // REFACTOR JWTがない時にログイン画面行くと、コンシェルジュリストの取得が失敗してログが出力されるのを防ぎたい。
  useConciergeList();
  useLoginUserName((pageProps as TPagePropsForJwtAuthentication).conciergeId);

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width,initial-scale=1,user-scalable=no,viewport-fit=cover" />
        <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;700&display=swap" rel="stylesheet" />
      </Head>
      <Component {...pageProps} />
    </>
  );
};

const goToErrorPage = <T,>(info: T, statusCode: number): [true, null] => {
  LogUtil.info<T>({ output: info });
  Router.push(`/${statusCode}`);
  return [true, null];
};

export const decideToGoToErrorPage = <T,>(
  result: TLwtApiResponse<T> | TResponseBodyOnError,
  id?: string,
): [boolean, T | null, TMeta?] => {
  const status = result.status;
  if (status === NOT_FOUND_ERROR || status === CONFLICT_ERROR) {
    return goToErrorPage(id, status);
  }
  if (result.isError) {
    return goToErrorPage(result, INTERNAL_SERVER_ERROR);
  }
  return [false, result.data, result.meta];
};

export default App;
